import React, { useEffect, useMemo, useRef, useState } from "react";
import Tabs from "../common/Tabs";
import BasicCalculations from "./basiccalculations";
import GasCalculations from "./gascalculations";
import Motor from "./motor";
import Pump from "./pump";
import Protector from "./protector";
import Cable from "./cable";
import SurfaceEquipment from "./surface-equipment";
import Schematics from "./schematic";
import WellSurvey from "./well-survey";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { usePumpFacade } from "../../facade/calculation/calculation-output/pump.facade";
import useProtectorFacade from "../../facade/calculation/calculation-output/protector.facade";
import useCableFacade from "../../facade/calculation/calculation-output/cable.facade";
import { useSurfaceEquipmentFacade } from "../../facade/calculation/calculation-output/surfaceEquipment.facade";
import { RootState } from "../../store/ConfigureStore";
import { useDispatch } from "react-redux";
import useWellSurveyFacade from "../../facade/calculation/calculation-output/well-survey.facade";
import { useParams } from "react-router-dom";
import useCalculationFacade from "../../facade/calculation/calculation.facade";

const CalculationSection: React.FC = () => {
    const { ID, caseID } = useParams();
    const projectID = ID?.split("projectID=")?.[1] || "";
    const caseId = caseID?.split("caseID=")?.[1] || "";
    const projectId = caseId || projectID;
    const [activeTab, setActiveTab] = useState<number>(0);
    const { tab3Input } = useAppSelector(state => state?.fluid);
    const { tab1Input } = useAppSelector(state => state?.tubing);

    const { SelectedPump } = useAppSelector(state => state?.pump);
    const { ProtectorList } = useAppSelector((state) => state?.protector);
    const {
        wellCableList,
        motorLeadList,
        wellEquipmentList,
        selectedWellEquipment,
    } = useAppSelector((state) => state?.cable)
    const VSDList = useAppSelector((state: RootState) => state.surfaceEquipment?.VSDList);
    const SelectedVSD = useAppSelector((state: RootState) => state.tubing?.tab1Input?.hiddenSurfVSD);
    const selectedProject = useAppSelector((state: RootState) => state.myProjects.selectedProject);
    const { selectedLowerEquipment } = useAppSelector((state) => state.protector);
    const { intakeMD } = useAppSelector((state) => state.calculations);

    const { updateSchematicEquipmentData } = useCalculationFacade();
    const {
        fetchMotorEquipment,
        fetchWellEquipment,
        refreshChart,
        updateSelectedWellEquipment
    } = useCableFacade();

    useEffect(() => {
        if (tab3Input?.gasAvailblty === "0" && activeTab === 0) {
            setActiveTab(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab3Input?.gasAvailblty])

    useEffect(() => {
        if (selectedLowerEquipment?.length === 0 && intakeMD && selectedWellEquipment?.length > 0) {
            const latestEquipment = wellEquipmentList && wellEquipmentList?.find((x: any) => x?.id === selectedWellEquipment?.[0]?.id);
            const updateWellEquipment = selectedWellEquipment?.length > 0 && selectedWellEquipment?.map((equipment: any) => {
                return {
                ...equipment,
                length: intakeMD,
                totalLength: intakeMD,
                dimensions: equipment.dimensions,
                nominalWeight: parseFloat(((intakeMD * parseFloat((latestEquipment?.nominalWeight || 0)?.toFixed(2)))?.toFixed(2)))
                }
            });
            updateSelectedWellEquipment(updateWellEquipment);
            updateSchematicEquipmentData();
        }
        if (intakeMD && selectedWellEquipment?.length > 0 && selectedLowerEquipment?.length >0) {
            const latestEquipment = wellEquipmentList && wellEquipmentList?.find((x: any) => x?.id === selectedWellEquipment?.[0]?.id);
            const updateWellEquipment = selectedWellEquipment?.length > 0 && selectedWellEquipment?.map((equipment: any) => {
                return {
                ...equipment,
                length: selectedLowerEquipment?.[0]?.setDepth,
                totalLength: selectedLowerEquipment?.[0]?.setDepth,
                dimensions: equipment.dimensions,
                nominalWeight: parseFloat(((selectedLowerEquipment?.[0]?.setDepth * parseFloat((latestEquipment?.nominalWeight || 0)?.toFixed(2)))?.toFixed(2)))
                }
            });
            updateSelectedWellEquipment(updateWellEquipment);
            updateSchematicEquipmentData();
        }
    }, [intakeMD]);

    // const selectedWellEquipmentSignature = useMemo(() => {
    //     if (!selectedWellEquipment || selectedWellEquipment.length === 0) {
    //         return "";
    //     }
    //     return JSON.stringify(
    //         selectedWellEquipment.map((equipment: any) => ({
    //             id: equipment?.id ?? null,
    //             length: equipment?.length ?? null,
    //             totalLength: equipment?.totalLength ?? null,
    //         }))
    //     );
    // }, [selectedWellEquipment]);

    // useEffect(() => {
    //     if (!selectedWellEquipmentSignature) {
    //         return;
    //     }
    //     // updateSchematicEquipmentData();
    // }, [selectedWellEquipmentSignature]);

    const {
        GetPumpEquipments,
    } = usePumpFacade();

    const {
        loadSelectedUpperProtector,
        loadSelectedLowerProtector
    } = useProtectorFacade();

    const { updatePowerField, updateWeightField } = useSurfaceEquipmentFacade();

    useEffect(() => {
        if (SelectedPump)
            GetPumpEquipments();
    }, [SelectedPump]);

    useEffect(() => {
        if (ProtectorList?.length > 0) {
            loadSelectedUpperProtector();
            loadSelectedLowerProtector();
        }
    }, [ProtectorList])

    const didRun = useRef(false);
    useEffect(() => {
        if (!didRun.current && tab1Input && wellCableList?.length > 0) {
            fetchMotorEquipment();
            fetchWellEquipment();
            refreshChart();
            didRun.current = true;
        }
    }, [wellCableList, motorLeadList, tab1Input])

    const didRunForVSD = useRef(false);
    useEffect(() => {
        if (!didRunForVSD.current && SelectedVSD && VSDList?.length) {
            const SelectedVSDData = VSDList.find((x: { vsD_ID: any; }) => x.vsD_ID === SelectedVSD?.toString());
            updatePowerField(SelectedVSDData?.powerField ?? 0);
            updateWeightField(SelectedVSDData?.weightField?.toString() || "");
            didRunForVSD.current = true;
        }
    }, [SelectedVSD, VSDList]);

    const dispatch = useDispatch();
    useEffect(() => {
        if (selectedProject?.projectID) {
            dispatch({ type: "wellSurvey/fetchExistingData", payload: { projectId } });
        }
    }, [selectedProject?.projectID, caseId]);

    const handleTabChange = (tab: number): void => {
        setActiveTab(tab);
    };

    const tabs = [
        {
            label: "Gas calculations",
            content: <GasCalculations />,
            isDisabled: tab3Input?.gasAvailblty === "0" ? true : false,
        },
        {
            label: "Basic calculations",
            content: <BasicCalculations />,
        },
        {
            label: "Pump",
            content: <Pump />,
        },
        {
            label: "Motor",
            content: <Motor />,
        },
        {
            label: "Protector",
            content: <Protector />,
        },
        {
            label: "Cable",
            content: <Cable />,
        },
        {
            label: "Surface equipment",
            content: <SurfaceEquipment />,
        },
        {
            label: "Schematics",
            content: <Schematics />,
        },
        {
            label: "Well survey",
            content: <WellSurvey />,
        },
    ];

    return (
        <div className="flex items-start flex-col justify-between flex-1 rounded-[8px] border-darkcolor-400 border-[1px] text-neutral-25 col-span-3 w-full h-full">
            <Tabs
                tabs={tabs}
                activeTab={activeTab}
                onChange={handleTabChange}
                className=""
            />
        </div>
    );
};

export default CalculationSection;
